import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import { sortPackageOptions } from 'lib/offer/offerPageUtil'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { min } from 'lib/array/arrayUtils'
import { formatInlineList } from 'lib/format/formatList'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getAvailablePackagesByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'

interface Params {
  room?: OfferPageRoom
}

interface SuggestedPackageResult {
  option: App.OfferPackageOptionView
  message: string
}

/**
 * Fallback recommendation when the backend does not return a usable package.
 */
function useSuggestedPackageOptionFromTotalSales({ room }: Params = {}): SuggestedPackageResult | undefined {
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const { rooms: allRooms } = offerPageState

  const packageOptionsAllRooms = useAppSelector(state => getDurationPackageOptions(offerPageState, state))
  const packageOptionsByRoom = useAppSelector(state => getAvailablePackagesByRoom(offerPageState, state))

  const packageOptions = room ? packageOptionsByRoom[room.id] : packageOptionsAllRooms

  // Recommend package based on overall highest sales.
  // Note: Booking count is only available at the room type level, so effectively this is the
  // cheapest option of the most popular room type.
  return useMemo(() => {
    const rooms = room ? [room] : allRooms
    const sortedPackageOptions = sortPackageOptions(packageOptions)

    const availablePackages = sortedPackageOptions.filter(option => option.capacityValid && option.hotelAvailable)
    if (availablePackages.length <= 1) {
      return undefined // It would be silly to highlight the most popular option if there was only one option...
    }
    const highestBookingCount = Math.max(...availablePackages.map(p => p.package.roomType.stats.booked))
    const mostPopularPackages = availablePackages.filter(p => p.package.roomType.stats.booked === highestBookingCount)
    const popularOption = min(mostPopularPackages, option => option.hotelPrice ?? Number.MAX_SAFE_INTEGER)

    if (popularOption) {
      const occupantsMessage = formatInlineList(formatOccupantsShort(rooms).split(', '), 'conjunction', { style: 'long' })
      return {
        option: popularOption,
        message: occupantsMessage ? intl.formatMessage(
          {
            id: 'PEh-afVr0xzcyZ_Sq0S2',
            defaultMessage: 'Popular for {occupants}',
            description: 'Promotional banner message indicating the most popular package option for the given room occupants',
          },
          { occupants: occupantsMessage },
        ) : '',
      }
    }

    return undefined
  }, [packageOptions, room, allRooms, intl])
}

export default useSuggestedPackageOptionFromTotalSales
